import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  MAX_BALLS,
  MAX_PASSPHRASE_LENGTH,
  MIN_BALLS,
  type GameMeta,
  isValidSlug,
} from '@photo-merge/shared';
import { Button } from '../components/Button';
import { PageHeader } from '../components/PageHeader';
import { Placeholder } from '../components/Placeholder';
import { ArrangeStep } from '../create/ArrangeStep';
import { CropEditor } from '../create/CropEditor';
import { loadPhotos, loadSavedBall, type Photo } from '../create/photos';
import { useSaveEdit } from '../create/useSaveEdit';
import type { CropTransform } from '../create/crop';
import { ApiError, checkEditPassphrase, fetchGame } from '../lib/api';
// Deliberately the create screen's stylesheet: this is the same photo step, so it
// should be the same card, the same fields, and the same buttons.
import styles from './Create.module.css';

/**
 * Changing a published game's photos.
 *
 * The roster is loaded back as the balls the game actually uses, so what you rearrange
 * here is what people are already playing with. Those balls can be moved or dropped but
 * not re-cropped — the browser threw the originals away when the game was made, and
 * what's stored is the finished circle.
 */
export function Edit() {
  const { slug } = useParams<{ slug: string }>();
  if (!slug || !isValidSlug(slug)) {
    return (
      <main className="page">
        <PageHeader title="Game not found" />
        <Placeholder
          phase="Bad link"
          what={`"${slug ?? ''}" isn't a valid game link`}
          detail="Check the link and try again."
        />
      </main>
    );
  }
  return <EditGame slug={slug} />;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; game: GameMeta }
  | { status: 'missing' }
  | { status: 'error'; message: string };

function EditGame({ slug }: { slug: string }) {
  const navigate = useNavigate();
  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  // Held in memory only, and only for as long as this screen is open. It's needed on
  // every request an edit makes, since the API re-checks it rather than issuing a
  // session of any kind.
  const [passphrase, setPassphrase] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [photos, setPhotos] = useState<Photo[] | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [rejected, setRejected] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const { progress, save } = useSaveEdit();

  const game = load.status === 'ready' ? load.game : null;
  const open = Boolean(game && (unlocked || !game.hasPassphrase));

  useEffect(() => {
    const controller = new AbortController();
    setLoad({ status: 'loading' });

    (async () => {
      try {
        const found = await fetchGame(slug, controller.signal);
        if (!controller.signal.aborted) setLoad({ status: 'ready', game: found });
      } catch (error) {
        if (controller.signal.aborted) return;
        if (error instanceof ApiError && error.status === 404) {
          setLoad({ status: 'missing' });
        } else {
          setLoad({
            status: 'error',
            message: error instanceof Error ? error.message : 'Could not load that game.',
          });
        }
      }
    })();

    return () => controller.abort();
  }, [slug]);

  // The roster images wait for the passphrase, so a locked game downloads nothing.
  useEffect(() => {
    if (!game || !open || photos) return;
    let cancelled = false;

    (async () => {
      try {
        const loaded = await Promise.all(game.balls.map(loadSavedBall));
        if (!cancelled) setPhotos(loaded);
      } catch {
        if (!cancelled) setRosterError('Could not load this game’s photos. Try reloading.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [game, open, photos]);

  async function handleFiles(list: FileList | null) {
    if (!list?.length || !photos) return;
    setBusy(true);
    setRejected([]);
    try {
      const result = await loadPhotos([...list], MAX_BALLS - photos.length);
      setPhotos([...photos, ...result.photos]);
      setRejected(result.rejected);
    } finally {
      setBusy(false);
      // Clearing lets the same file be picked again after a removal.
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function commitCrop(crop: CropTransform) {
    setPhotos(
      (current) =>
        current?.map((photo, i) => (i === editing ? { ...photo, crop } : photo)) ?? null,
    );
    setEditing(null);
  }

  async function handleSave() {
    if (!photos) return;
    const saved = await save(slug, passphrase, photos);
    if (saved) navigate(`/play/${slug}`, { replace: true });
  }

  if (load.status === 'loading') {
    return (
      <main className="page">
        <PageHeader title="Edit photos" subtitle={slug} backTo={`/play/${slug}`} />
        <p className={styles.intro}>Looking up this game…</p>
      </main>
    );
  }

  if (load.status === 'missing') {
    return (
      <main className="page">
        <PageHeader title="Game not found" subtitle={slug} />
        <Placeholder
          phase="Not found"
          what={`No game lives at /play/${slug}`}
          detail="There's nothing here to edit."
        />
      </main>
    );
  }

  if (load.status === 'error') {
    return (
      <main className="page">
        <PageHeader title="Something went wrong" subtitle={slug} />
        <Placeholder phase="Error" what={load.message} detail="Try reloading the page." />
      </main>
    );
  }

  const { game: meta } = load;

  if (!open) {
    return (
      <main className="page">
        <PageHeader
          title={meta.title || 'Edit photos'}
          subtitle={`/play/${slug}`}
          backTo={`/play/${slug}`}
        />
        <PassphraseGate
          slug={slug}
          onUnlock={(value) => {
            setPassphrase(value);
            setUnlocked(true);
          }}
        />
      </main>
    );
  }

  if (rosterError) {
    return (
      <main className="page">
        <PageHeader title="Edit photos" subtitle={`/play/${slug}`} backTo={`/play/${slug}`} />
        <Placeholder phase="Error" what={rosterError} detail="Try reloading the page." />
      </main>
    );
  }

  if (!photos) {
    return (
      <main className="page">
        <PageHeader title="Edit photos" subtitle={`/play/${slug}`} backTo={`/play/${slug}`} />
        <p className={styles.intro}>Fetching this game’s photos…</p>
      </main>
    );
  }

  const saving = progress.phase !== 'idle' && progress.phase !== 'error';
  const enoughPhotos = photos.length >= MIN_BALLS;
  const changed =
    photos.length !== meta.balls.length ||
    photos.some((photo, index) => photo.savedKey !== meta.balls[index]);

  return (
    <main className="page">
      <PageHeader
        title={meta.title || 'Edit photos'}
        subtitle={`/play/${slug}`}
        backTo={`/play/${slug}`}
      />

      <div className={styles.steps}>
        <section className={styles.step}>
          <div className={styles.stepHead}>
            <h2 className={styles.stepTitle}>
              Photos ({photos.length}/{MAX_BALLS})
            </h2>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="visually-hidden"
            onChange={(e) => void handleFiles(e.target.files)}
          />

          {photos.length ? (
            <ArrangeStep
              photos={photos}
              onReorder={setPhotos}
              onEdit={setEditing}
              onRemove={(index) => setPhotos(photos.filter((_, i) => i !== index))}
            />
          ) : (
            <p className={styles.intro}>
              Every photo is gone. Add at least {MIN_BALLS} to save, or go back to leave
              the game as it was.
            </p>
          )}

          {rejected.length ? (
            <p className={styles.warning}>
              Couldn&apos;t read {rejected.length === 1 ? 'this photo' : 'these photos'}:{' '}
              {rejected.join(', ')}. If they came from an iPhone they may be HEIC — try
              re-picking, or screenshot them first.
            </p>
          ) : null}

          <div className={styles.tools}>
            <Button
              variant="secondary"
              disabled={busy || saving || photos.length >= MAX_BALLS}
              onClick={() => fileRef.current?.click()}
            >
              {busy ? 'Reading…' : 'Add more'}
            </Button>
          </div>
        </section>

        <div className={styles.saveBar}>
          {progress.phase === 'error' ? (
            <p className={styles.warning}>{progress.message}</p>
          ) : null}
          {saving ? (
            <div className={styles.progress} role="status">
              <div className={styles.progressBar}>
                <span style={{ width: `${Math.round(progress.fraction * 100)}%` }} />
              </div>
              <span className={styles.progressLabel}>{progress.message}</span>
            </div>
          ) : null}

          <Button
            block
            disabled={saving || !enoughPhotos || !changed}
            onClick={() => void handleSave()}
          >
            {saving
              ? 'Saving…'
              : !enoughPhotos
                ? `Keep at least ${MIN_BALLS} photos`
                : !changed
                  ? 'No changes yet'
                  : 'Save changes'}
          </Button>
          <Button
            variant="secondary"
            block
            disabled={saving}
            onClick={() => navigate(`/play/${slug}`)}
          >
            Back to the game
          </Button>
          <p className={styles.hint}>
            Scores are kept. Anyone already playing keeps the old photos until they
            reload.
          </p>
        </div>
      </div>

      {editing != null && photos[editing] ? (
        <CropEditor
          photo={photos[editing]}
          rank={editing + 1}
          count={photos.length}
          onCommit={commitCrop}
          onCancel={() => setEditing(null)}
        />
      ) : null}
    </main>
  );
}

/** Stands between a passphrase-protected game and its roster. */
function PassphraseGate({
  slug,
  onUnlock,
}: {
  slug: string;
  onUnlock: (passphrase: string) => void;
}) {
  const [value, setValue] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!value.trim() || checking) return;
    setChecking(true);
    setError(null);
    try {
      await checkEditPassphrase(slug, value);
      onUnlock(value);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not check that passphrase.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <form className={styles.step} onSubmit={(e) => void submit(e)}>
      <div className={styles.stepHead}>
        <h2 className={styles.stepTitle}>This game&apos;s photos are locked</h2>
      </div>
      <p className={styles.intro}>
        Enter the passphrase set when the game was made to change its photos.
      </p>

      <label className={styles.field}>
        <span className={styles.fieldLabel}>Passphrase</span>
        <input
          className={styles.text}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={MAX_PASSPHRASE_LENGTH}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          autoFocus
        />
      </label>

      {error ? <p className={styles.warning}>{error}</p> : null}

      <div className={styles.tools}>
        <Button type="submit" disabled={checking || !value.trim()}>
          {checking ? 'Checking…' : 'Unlock'}
        </Button>
      </div>
    </form>
  );
}
